import { useEffect, useState } from "react";
import axios from "axios";
import {
  X, Rocket, ArrowRight, ArrowLeft, CheckCircle2, Building2, Palette, Layers,
  Loader2, Sparkles,
} from "lucide-react";
import { API } from "@/lib/api";

const NEON = "#22C55E";
const NEON_HI = "#4ADE80";
const GOLD = "#F5B800";
const PANEL = "#0F0F13";
const LINE = "rgba(255,255,255,0.10)";
const DIM = "rgba(255,255,255,0.7)";
const FADE = "rgba(255,255,255,0.4)";

const INDUSTRIES = [
  "Auto repair", "Roofing", "Plumbing", "HVAC", "Landscaping", "Pest control",
  "Law firm", "Medical clinic", "Dental", "Restaurant", "Salon / Spa",
  "Real estate", "Insurance", "Construction", "Cleaning", "Moving / Freight",
  "Retail", "Fitness", "Coaching", "Other",
];

const MODULES = [
  { key: "website",    label: "Website",         desc: "Full CMS + hosting" },
  { key: "crm",        label: "CRM & Leads",     desc: "One unified inbox" },
  { key: "salesbot",   label: "SalesBot",        desc: "24/7 AI that closes" },
  { key: "supportbot", label: "Support Bot",     desc: "AI answers customer FAQs" },
  { key: "scheduling", label: "Scheduling",      desc: "Calendar + reminders" },
  { key: "email_sms",  label: "Email + SMS",     desc: "Automated follow-up" },
  { key: "seo",        label: "SEO Engine",      desc: "Schema · sitemap · programmatic" },
  { key: "analytics",  label: "Analytics",       desc: "Traffic + attribution" },
  { key: "billing",    label: "Billing",         desc: "Stripe checkout + invoices" },
];

const BUDGETS = ["Launch tier ($299.99)", "$500–$2k", "$2k–$5k", "$5k–$12k", "$12k+"];
const TARGETS = ["ASAP (1–2 weeks)", "Within a month", "1–3 months", "3+ months"];

const EMPTY = {
  company_name: "",
  industry: "",
  contact_name: "",
  email: "",
  phone: "",
  website: "",
  primary_color: "#22C55E",
  modules: ["website", "crm", "salesbot"],
  monthly_budget: "",
  launch_target: "",
  notes: "",
};

export default function OnboardCompanyModal({ onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null); // { lead_id, message }

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose, submitting]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleModule = (k) => setForm((f) => ({
    ...f,
    modules: f.modules.includes(k) ? f.modules.filter((m) => m !== k) : [...f.modules, k],
  }));

  const step1Valid = form.company_name.trim() && form.industry.trim();
  const step2Valid = form.contact_name.trim() && form.email.trim() && form.phone.trim();
  const step3Valid = form.modules.length > 0;

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/public/onboard-request`, form);
      setDone(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6" data-testid="onboard-modal">
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(4px)" }}
        onClick={() => !submitting && onClose()}
      />

      <div
        className="relative w-full max-w-[720px] max-h-[92vh] rounded-xl overflow-hidden flex flex-col animate-popIn"
        style={{
          background: "#08080B",
          border: `1px solid ${LINE}`,
          boxShadow: `0 40px 100px -30px rgba(0,0,0,0.8), 0 0 60px -20px ${NEON}44`,
        }}
        data-testid="onboard-modal-inner"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: LINE }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: PANEL, border: `1px solid ${NEON}55` }}>
              <Rocket size={16} style={{ color: NEON }} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-black" style={{ color: FADE }}>Onboarding · New company</div>
              <div className="font-black text-sm text-white">Provision the full stack</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="w-9 h-9 rounded-md flex items-center justify-center hover:bg-white/5"
            style={{ color: DIM }}
            data-testid="onboard-close"
          >
            <X size={16} />
          </button>
        </div>

        {done ? (
          <SuccessScreen done={done} onClose={onClose} form={form} />
        ) : (
          <>
            {/* Step tracker */}
            <div className="px-6 py-4 border-b flex items-center gap-2" style={{ borderColor: LINE }}>
              {[
                { n: 1, label: "Company", icon: Building2 },
                { n: 2, label: "Contact",  icon: Layers },
                { n: 3, label: "Modules",  icon: Palette },
                { n: 4, label: "Review",   icon: CheckCircle2 },
              ].map((s, i) => {
                const active = step === s.n;
                const done = step > s.n;
                return (
                  <div key={s.n} className="flex items-center flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                           style={{
                             background: done ? NEON : (active ? "rgba(34,197,94,0.15)" : PANEL),
                             color: done ? "#0A0A0C" : (active ? NEON : FADE),
                             border: `1px solid ${done ? NEON : (active ? NEON + "88" : LINE)}`,
                           }}>
                        <s.icon size={12} />
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.22em] font-black truncate"
                           style={{ color: active ? "#FFF" : (done ? NEON : FADE) }}>
                        {s.n}. {s.label}
                      </div>
                    </div>
                    {i < 3 && <div className="mx-2 flex-1 h-px" style={{ background: done ? NEON : LINE }} />}
                  </div>
                );
              })}
            </div>

            {/* Body */}
            <div className="px-6 py-6 overflow-y-auto flex-1" style={{ color: "#FFF" }}>
              {step === 1 && (
                <div className="space-y-4" data-testid="onboard-step-1">
                  <Field label="Company name" required>
                    <Input value={form.company_name} onChange={set("company_name")} placeholder="Acme Roofing Co." testId="onboard-company-name" />
                  </Field>
                  <Field label="Industry" required>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {INDUSTRIES.map((ind) => {
                        const active = form.industry === ind;
                        return (
                          <button
                            key={ind}
                            type="button"
                            onClick={() => set("industry")(ind)}
                            className="rounded-md px-3 py-2 text-xs text-left border transition-all"
                            style={{
                              background: active ? "rgba(34,197,94,0.14)" : PANEL,
                              borderColor: active ? NEON : LINE,
                              color: active ? "#FFF" : DIM,
                              fontWeight: active ? 800 : 500,
                            }}
                            data-testid={`onboard-industry-${ind.toLowerCase().replace(/\s+/g, "-").replace("/", "")}`}
                          >
                            {ind}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                  <Field label="Existing website (optional)">
                    <Input value={form.website} onChange={set("website")} placeholder="https://acmeroofing.com" testId="onboard-website" />
                  </Field>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4" data-testid="onboard-step-2">
                  <Field label="Your name" required>
                    <Input value={form.contact_name} onChange={set("contact_name")} placeholder="Chris Garcia" testId="onboard-contact-name" />
                  </Field>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Email" required>
                      <Input value={form.email} onChange={set("email")} type="email" placeholder="you@company.com" testId="onboard-email" />
                    </Field>
                    <Field label="Phone" required>
                      <Input value={form.phone} onChange={set("phone")} placeholder="(214) 555-1234" testId="onboard-phone" />
                    </Field>
                  </div>
                  <Field label="Primary brand color">
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={form.primary_color}
                        onChange={(e) => set("primary_color")(e.target.value)}
                        className="w-14 h-11 rounded-md cursor-pointer border"
                        style={{ borderColor: LINE, background: PANEL }}
                        data-testid="onboard-color"
                      />
                      <Input value={form.primary_color} onChange={set("primary_color")} placeholder="#22C55E" testId="onboard-color-hex" />
                    </div>
                  </Field>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4" data-testid="onboard-step-3">
                  <div className="text-xs" style={{ color: DIM }}>
                    Pick every module you want provisioned on day 1. You can toggle more later.
                  </div>
                  <div className="grid md:grid-cols-2 gap-2">
                    {MODULES.map((m) => {
                      const on = form.modules.includes(m.key);
                      return (
                        <button
                          key={m.key}
                          type="button"
                          onClick={() => toggleModule(m.key)}
                          className="text-left rounded-md p-3 border transition-all flex items-start gap-3"
                          style={{
                            background: on ? "rgba(34,197,94,0.10)" : PANEL,
                            borderColor: on ? NEON : LINE,
                          }}
                          data-testid={`onboard-module-${m.key}`}
                        >
                          <div className="w-4 h-4 rounded-sm flex items-center justify-center shrink-0 mt-0.5"
                               style={{ background: on ? NEON : "transparent", border: `1px solid ${on ? NEON : "rgba(255,255,255,0.3)"}` }}>
                            {on && <CheckCircle2 size={12} style={{ color: "#0A0A0C" }} />}
                          </div>
                          <div>
                            <div className="text-sm font-black" style={{ color: on ? "#FFF" : DIM }}>{m.label}</div>
                            <div className="text-[11px] mt-0.5" style={{ color: FADE }}>{m.desc}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-2">
                    <Field label="Monthly budget">
                      <Select value={form.monthly_budget} onChange={set("monthly_budget")} options={BUDGETS} testId="onboard-budget" />
                    </Field>
                    <Field label="Launch target">
                      <Select value={form.launch_target} onChange={set("launch_target")} options={TARGETS} testId="onboard-target" />
                    </Field>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-4" data-testid="onboard-step-4">
                  <div className="text-xs uppercase tracking-[0.24em] font-black" style={{ color: FADE }}>Review + notes</div>
                  <div className="rounded-md p-4 border grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]"
                       style={{ background: PANEL, borderColor: LINE }}>
                    <ReviewRow label="Company" value={form.company_name} />
                    <ReviewRow label="Industry" value={form.industry} />
                    <ReviewRow label="Contact" value={form.contact_name} />
                    <ReviewRow label="Email" value={form.email} />
                    <ReviewRow label="Phone" value={form.phone} />
                    <ReviewRow label="Website" value={form.website || "—"} />
                    <ReviewRow label="Brand color" value={form.primary_color} swatch={form.primary_color} />
                    <ReviewRow label="Modules" value={`${form.modules.length} selected`} />
                    <ReviewRow label="Budget" value={form.monthly_budget || "—"} />
                    <ReviewRow label="Launch" value={form.launch_target || "—"} />
                  </div>
                  <Field label="Anything else Kit should know?">
                    <textarea
                      value={form.notes}
                      onChange={(e) => set("notes")(e.target.value)}
                      rows={4}
                      placeholder="Existing systems, integrations, pain points, deadlines…"
                      className="w-full rounded-md px-3 py-2 text-sm outline-none border resize-y"
                      style={{ background: PANEL, borderColor: LINE, color: "#FFF" }}
                      data-testid="onboard-notes"
                    />
                  </Field>
                  {error && (
                    <div className="text-xs rounded-md px-3 py-2 border"
                         style={{ background: "rgba(220,38,38,0.08)", borderColor: "rgba(220,38,38,0.4)", color: "#FCA5A5" }}
                         data-testid="onboard-error">
                      {error}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer nav */}
            <div className="px-6 py-4 border-t flex items-center justify-between gap-3" style={{ borderColor: LINE }}>
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1 || submitting}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-[11px] uppercase tracking-[0.22em] font-black border"
                style={{ borderColor: LINE, color: step === 1 ? FADE : DIM, opacity: step === 1 ? 0.4 : 1 }}
                data-testid="onboard-back"
              >
                <ArrowLeft size={12} /> Back
              </button>
              {step < 4 ? (
                <button
                  type="button"
                  onClick={() => setStep((s) => s + 1)}
                  disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid) || (step === 3 && !step3Valid)}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md text-[11px] uppercase tracking-[0.22em] font-black"
                  style={{
                    background: NEON,
                    color: "#0A0A0C",
                    opacity:
                      (step === 1 && !step1Valid) || (step === 2 && !step2Valid) || (step === 3 && !step3Valid) ? 0.4 : 1,
                  }}
                  data-testid="onboard-next"
                >
                  Continue <ArrowRight size={12} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md text-[11px] uppercase tracking-[0.22em] font-black"
                  style={{
                    background: GOLD,
                    color: "#0A0A0C",
                    opacity: submitting ? 0.6 : 1,
                    boxShadow: `0 14px 34px -14px ${GOLD}88`,
                  }}
                  data-testid="onboard-submit"
                >
                  {submitting ? <><Loader2 size={12} className="animate-spin" /> Submitting…</> : <><Rocket size={12} /> Provision stack →</>}
                </button>
              )}
            </div>
          </>
        )}

        <style>{`
          @keyframes popIn { from { opacity: 0; transform: scale(0.96) translateY(10px);} to { opacity:1; transform: scale(1) translateY(0);} }
          .animate-popIn { animation: popIn 240ms cubic-bezier(0.16,1,0.3,1) both; }
        `}</style>
      </div>
    </div>
  );
}

function SuccessScreen({ done, onClose, form }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8 py-14 text-center" data-testid="onboard-success">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
           style={{ background: `${NEON}22`, border: `1px solid ${NEON}`, boxShadow: `0 0 40px ${NEON}55` }}>
        <Sparkles size={26} style={{ color: NEON }} />
      </div>
      <div className="text-[10px] uppercase tracking-[0.32em] font-black mb-2" style={{ color: NEON }}>
        Request received
      </div>
      <h3 className="font-black text-2xl md:text-3xl leading-tight text-white"
          style={{ fontFamily: "'Cabinet Grotesk','Inter',system-ui,sans-serif" }}>
        {form.company_name} is in the queue.
      </h3>
      <p className="mt-3 text-sm max-w-[420px]" style={{ color: DIM }}>
        {done.message || "Kit will reach out within 24 hours to lock the launch date and provision your stack."}
      </p>
      <div className="mt-6 text-[10px] uppercase tracking-[0.24em]" style={{ color: FADE }}>
        Reference · {String(done.lead_id || "").slice(0, 8)}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="mt-8 inline-flex items-center gap-2 h-11 px-6 rounded-md text-[11px] uppercase tracking-[0.24em] font-black"
        style={{ background: NEON, color: "#0A0A0C" }}
        data-testid="onboard-success-close"
      >
        Back to hub →
      </button>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.24em] font-black mb-1.5" style={{ color: FADE }}>
        {label} {required && <span style={{ color: NEON }}>*</span>}
      </div>
      {children}
    </label>
  );
}

function Input({ value, onChange, placeholder, type = "text", testId }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-11 rounded-md px-3 text-sm outline-none border transition-all"
      style={{ background: PANEL, borderColor: LINE, color: "#FFF" }}
      onFocus={(e) => { e.target.style.borderColor = NEON + "88"; e.target.style.boxShadow = `0 0 0 3px ${NEON}22`; }}
      onBlur={(e) => { e.target.style.borderColor = LINE; e.target.style.boxShadow = "none"; }}
      data-testid={testId}
    />
  );
}

function Select({ value, onChange, options, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-11 rounded-md px-3 text-sm outline-none border"
      style={{ background: PANEL, borderColor: LINE, color: value ? "#FFF" : FADE }}
      data-testid={testId}
    >
      <option value="">Choose…</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function ReviewRow({ label, value, swatch }) {
  return (
    <>
      <div className="uppercase tracking-[0.2em] text-[10px] font-black" style={{ color: FADE }}>{label}</div>
      <div className="text-white flex items-center gap-2 text-[12px]">
        {swatch && <span className="w-3.5 h-3.5 rounded-sm inline-block" style={{ background: swatch, border: "1px solid rgba(255,255,255,0.2)" }} />}
        <span>{value}</span>
      </div>
    </>
  );
}
